/**
 * The office's contact details, as one canonical constant.
 *
 * FR-068 needs a phone number the customer can actually reach, and the panel had
 * none. The system already carries TWO hand-kept copies of these numbers — the
 * mail footers in the backend and `front/src/app/services/footer-static.service.ts`
 * in the admin panel — so this is the third and last place they may appear on this
 * side. It deliberately lives in `core/` rather than inside the registrations
 * feature: S-17 (contact-page, FR-066) consumes this constant and imports it
 * rather than starting copy number four.
 *
 * Zero backend involvement is intentional (`shape-notes.md:321-322`): these values
 * change roughly never, and an endpoint for them would be a deployment dependency
 * bought for nothing.
 *
 * The values come from TWO sources, and every one of them names its own below:
 * the admin panel's mail footers (`front/src/app/services/footer-static.service.ts`,
 * read verbatim) and the company's public website (`https://www.empiria.edu.pl/kontakt`,
 * read 2026-08-13) — the latter being the only source for the office hours, which no
 * file in any of the three repos carries.
 */

export interface ContactPhone {
  /** Who picks up — part of the link's accessible name, never a bare number. */
  readonly label: string;
  /** Grouped the way the footers print it: for reading, not for dialling. */
  readonly display: string;
  /** What a phone actually dials. E.164, because `tel:` has no notion of "91 434…". */
  readonly href: string;
}

export interface ContactAddress {
  readonly street: string;
  readonly postalCode: string;
  readonly city: string;
}

export interface ContactEmail {
  /** Who reads the mailbox — part of the link's accessible name, never a bare address. */
  readonly label: string;
  /** What a human reads and can copy by eye, without the `mailto:` scheme in the way. */
  readonly address: string;
  /** What a mail client actually opens. Always `mailto:` + `address`, never anything else. */
  readonly href: string;
}

export interface OfficeHours {
  /** Which days the office answers, so the screen can say it in a Polish sentence. */
  readonly days: string;
  /** Between which hours it answers, kept apart from the days for that same sentence. */
  readonly hours: string;
}

export const EMPIRIA_CONTACT = {
  companyName: 'Salon Edukacyjny EMPIRIA Gabriela Helińska Sp.k.',
  address: {
    street: 'ul. W. Broniewskiego 12',
    postalCode: '71-460',
    city: 'Szczecin',
  } satisfies ContactAddress,
  phones: {
    secretariat: {
      label: 'Sekretariat',
      display: '91 434 00 01',
      href: 'tel:+48914340001',
    },
    openCoursesCoordinator: {
      label: 'Koordynator szkoleń otwartych',
      display: '736 849 535',
      href: 'tel:+48736849535',
    },
    closedCoursesCoordinator: {
      label: 'Koordynator szkoleń zamkniętych',
      display: '579 641 522',
      href: 'tel:+48579641522',
    },
    // `front/src/app/services/footer-static.service.ts:16` („Księgowość/faktury tel.: 736 855 624")
    // and `https://www.empiria.edu.pl/kontakt` (read 2026-08-13, heading „Księgowość i finanse").
    accounting: {
      label: 'Księgowość i faktury',
      display: '736 855 624',
      href: 'tel:+48736855624',
    },
    // `https://www.empiria.edu.pl/kontakt` (read 2026-08-13, heading „Dyrektor ODN —
    // Gabriela Helińska") AND `front/src/app/services/footer-static.service.ts:30`
    // (`openCourseFooter`: „tel./fax. (91) 434 00 01; +48 693 543 256") — one line above the
    // e-mail cited below, and confirmed by re-grepping the three repos during S-17 impl-review.
    // The plan and this comment previously claimed no repo file carried this number; that was
    // false, and `officeHours` is the ONLY value here that no file in any repo confirms.
    // Product owner decided 2026-08-13 to publish it, „just like the company website does".
    director: {
      label: 'Dyrektor ODN',
      display: '693 543 256',
      href: 'tel:+48693543256',
    },
  } satisfies Readonly<Record<string, ContactPhone>>,
  emails: {
    // NO ENTRY FOR CLOSED COURSES, and that is a MEASURED absence — not an oversight.
    //
    // The FIRST answer to Q-146 (2026-08-14) said to publish whatever the open- and
    // closed-course mail footers carry — it was executed, it produced the conflict recorded
    // below, and the SECOND answer to Q-146 (same day, quoted above `office`) settled it the
    // other way: one address in every category. The search result stays here because it is
    // the map back to the per-category addresses, for the day the owner repoints them.
    // Every footer was read on 2026-08-14, and this is the whole of it:
    //   · `backend/assets/static_mail_content/open_registration_confirm_footer.txt:8-11`
    //     (fallback footer of „Zgłoszenie na szkolenie otwarte") — four phones, ALL of them
    //     already in `phones` below, and NOT ONE e-mail address;
    //   · `backend/assets/static_mail_content/closed_registration_confirm_footer.txt:16`
    //     (fallback footer of „Zgłoszenie na szkolenie zamknięte") — exactly one address,
    //     `bozenapaprota@empiria.edu.pl`, signed „Bożena Paprota";
    //   · `front/src/app/services/footer-static.service.ts:31` (`openCourseFooter`) — the
    //     `director` entry below; `:45` (`closedCourseFooter`) — Paprota's address again.
    //
    // That single new address is the one Q-121/A took OFF this constant on 2026-08-13
    // („Wrzuć Jankowską oraz jej adres email"; Q-123/A: „Jankowska zamknięte"): the person
    // was REPLACED, so both closed-course footers are stale against the product owner's own
    // decision, and putting the address back would publish a mailbox we were told is gone.
    // The two answers cannot both be executed, so the conflict is ESCALATED, not resolved
    // here, and nothing was added. No address for Jankowska exists in ANY of the three repos
    // (grepped 2026-08-14) — and one derived from the `imienazwisko@` pattern would be an
    // invention landing on a public screen, which is the one thing Q-146 forbade outright.
    //
    // Two near-misses, recorded so the next reader does not re-mine the same ground:
    // `ankiety_ex_ante@empiria.edu.pl` (`…/closed_registration_confirm.txt:7`) is real and
    // functional, but it stands in the BODY rather than the footer and serves ONE errand —
    // returning the ex-ante survey — so it is not a contact channel. And
    // `szkoleniazamkniete@` / `szkoleniaotwarte@empiria.edu.pl` (`backend/src/services/
    // facades/smtp.mailer.ts:31-32`) are role mailboxes with SMTP credentials, but NO code
    // path sends from them — both confirmations actually leave as `szkolenia@edumyslna.pl`
    // (`register-closed-course.ts:182`, `register-open-course.ts:314`), so nothing here
    // shows they are read.
    //
    // ONE ADDRESS IN EVERY CATEGORY, AND THAT IS A DECISION — not a copy-paste slip.
    //
    // Q-146/A (2026-08-14), verbatim: „daj w takim razie wszędzie gabrielahelinska i później
    // ustalę i sobie podmienimy — to można zrobić na samym końcu przecież". So every entry
    // below carries the SAME address, and the categories (their `label`s) survive precisely
    // because the product owner intends to repoint them one by one later: swapping a value
    // back is one line, resurrecting a deleted key is a decision nobody has taken.
    //
    // What this entry held until 2026-08-14 was `biuro@empiria.edu.pl`, the only address the
    // company publishes on `https://www.empiria.edu.pl/kontakt` (read 2026-08-13; not in the
    // visible text — the page cloaks it Joomla-style, assembling it in a script from HTML
    // entities, and the value was what that script decoded to). It is recorded here rather
    // than dropped, because the swap-back the owner announced needs a source to swap back TO.
    //
    // CONSEQUENCE FOR THE SCREEN, written down because it bit the specs: `href` is no longer
    // a unique identity for an e-mail entry. Anything keying entries by `mailto:` — the
    // `@for` track expression, a test's lookup map — has to key by label instead, or it
    // silently collapses two published channels into one.
    office: {
      label: 'Biuro',
      address: 'gabrielahelinska@empiria.edu.pl',
      href: 'mailto:gabrielahelinska@empiria.edu.pl',
    },
    // `front/src/app/services/footer-static.service.ts:31` (`openCourseFooter`) — read
    // verbatim, not remembered.
    director: {
      label: 'Dyrektor ODN',
      address: 'gabrielahelinska@empiria.edu.pl',
      href: 'mailto:gabrielahelinska@empiria.edu.pl',
    },
  } satisfies Readonly<Record<string, ContactEmail>>,
  // `https://www.empiria.edu.pl/kontakt`, read 2026-08-13, which says verbatim: „Jesteśmy do
  // Państwa dyspozycji od poniedziałku do piątku w godzinach: 8.00 - 16.00". This is the ONE
  // value in this file that NO file in ANY of the three repos confirms — the admin panel does
  // not carry office hours and neither does the backend, both grepped. A reader asking in a
  // year „how do we know this" has to get the answer from the file, not from someone's memory.
  // Reformatted for a Polish reader (en dashes, `8:00` not `08.00`), not copied byte for byte.
  officeHours: {
    days: 'poniedziałek – piątek',
    hours: '8:00 – 16:00',
  } satisfies OfficeHours,
} as const;

/**
 * Who the customer calls to cancel a submission (FR-068).
 *
 * THE one point steered by the answer to Q-S3-4. Today it is the secretariat for
 * both kinds of submission, because the secretariat is the number the customer
 * already has from every e-mail footer and it routes internally. Should Filip want
 * the coordinator that matches the submission's `kind`, this alias becomes a
 * `kind => ContactPhone` function and nothing else in the slice moves — the details
 * screen reads it through one accessor.
 */
export const CANCELLATION_CONTACT: ContactPhone = EMPIRIA_CONTACT.phones.secretariat;
